import { useState } from "react";
import {
  Wrench, X, Coins, Package, Map, Gift, Trash2, Copy, Check, Sun,
  CloudRain, RotateCcw, ImageIcon, PlusSquare,
} from "lucide-react";
import { CATALOG, BY_ID, isNpc } from "./catalog";
import { SETS, setProgress } from "./sets";
import { RARITIES, RARITY_ORDER, MAX_CHUNKS, chunkPrice, priceOf, fmt } from "./economy";
import { CYCLE_MS } from "./sky";
import AdminImages from "./AdminImages";
import AdminCreate from "./AdminCreate";
import { t } from "@/i18n";

// ─────────────────────────────────────────────────────────────
// PANNEAU ADMIN
//
// L'accès suit le rôle réel du compte connecté (voir Rewards.jsx, qui
// lit user.role via useAuth()) — jamais un paramètre d'URL.
//
// Les actions « Économie / Objets / Carte » ne touchent que l'état
// local : le serveur reste maître du solde et de l'inventaire.
// En revanche, l'ATELIER et les IMAGES écrivent bien en base, sous
// contrôle du rôle admin côté serveur : c'est du contenu de jeu,
// partagé par tous les joueurs, pas de la triche locale.
// ─────────────────────────────────────────────────────────────

const Section = ({ title, icon: Icon, children }) => (
  <div className="space-y-2">
    <div className="flex items-center gap-2 text-xs font-bold uppercase tracking-wider text-muted-foreground">
      <Icon size={13} /> {title}
    </div>
    {children}
  </div>
);

const Btn = ({ children, onClick, tone = "default" }) => (
  <button
    type="button"
    onClick={onClick}
    className={`rounded-lg px-2.5 py-1.5 text-xs font-semibold transition ${
      tone === "danger"
        ? "bg-red-500/15 text-red-600 hover:bg-red-500/25"
        : tone === "primary"
          ? "bg-primary text-primary-foreground hover:brightness-110"
          : "bg-muted hover:bg-muted/70"
    }`}
  >
    {children}
  </button>
);

export default function AdminPanel({
  balance, setBalance, inventory, setInventory, island, setIsland,
  pity, setPity, onGrantSet, onOpenBox, say, calibrate, setCalibrate,
  adminSky, setAdminSky, onResetIsland, onImagesChanged,
}) {
  const [open, setOpen] = useState(false);
  const [imagesOpen, setImagesOpen] = useState(false);
  const [createOpen, setCreateOpen] = useState(false);
  const [tab, setTab] = useState("eco");
  const [filter, setFilter] = useState("");
  const [copied, setCopied] = useState(false);

  const grant = (id, n = 1) =>
    setInventory((inv) => ({ ...inv, [id]: (inv[id] || 0) + n }));

  const grantAll = () =>
    setInventory(Object.fromEntries(CATALOG.map((i) => [i.id, 3])));

  const addChunks = (n) => {
    setIsland((is) => {
      const owned = new Set(is.chunks.map((c) => `${c.x},${c.y}`));
      const next = [...is.chunks];
      let guard = 0;
      while (next.length < Math.min(MAX_CHUNKS, is.chunks.length + n) && guard++ < 400) {
        let added = false;
        for (const c of [...next]) {
          for (const [dx, dy] of [[1, 0], [0, 1], [-1, 0], [0, -1]]) {
            const k = `${c.x + dx},${c.y + dy}`;
            if (owned.has(k)) continue;
            owned.add(k);
            next.push({ x: c.x + dx, y: c.y + dy });
            added = true;
            break;
          }
          if (added || next.length >= Math.min(MAX_CHUNKS, is.chunks.length + n)) break;
        }
        if (!added) break;
      }
      return { ...is, chunks: next };
    });
  };

  const copyState = () => {
    const payload = JSON.stringify({ island, inventory, balance, pity }, null, 2);
    navigator.clipboard?.writeText(payload).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    });
  };

  const items = CATALOG.filter(
    (i) => !filter || i.name.toLowerCase().includes(filter.toLowerCase()) || i.id.includes(filter.toLowerCase())
  );

  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        title={t("adminpanel.panneau_admin")}
        className="fixed bottom-5 right-5 z-[90] w-12 h-12 grid place-items-center rounded-2xl bg-slate-900 text-white shadow-xl hover:scale-105 transition"
      >
        <Wrench size={20} />
      </button>
    );
  }

  return (
    <div className="fixed bottom-0 right-0 sm:bottom-5 sm:right-5 z-[90] w-full sm:w-[420px] max-h-[80vh] overflow-y-auto rounded-t-3xl sm:rounded-3xl bg-card border shadow-2xl">
      <div className="sticky top-0 bg-card border-b p-4 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <Wrench size={16} className="text-primary" />
          <span className="font-head font-bold">{t("adminpanel.panneau_admin")}</span>
        </div>
        <button type="button" onClick={() => setOpen(false)} className="rounded-full p-1.5 hover:bg-muted">
          <X size={16} />
        </button>
      </div>

      <div className="flex gap-1 p-2 border-b">
        {[["eco", "Économie"], ["items", "Objets"], ["sets", "Confréries"], ["map", "Carte"]].map(([id, label]) => (
          <button key={id} type="button" onClick={() => setTab(id)}
            className={`flex-1 rounded-lg px-2 py-1.5 text-xs font-semibold transition ${
              tab === id ? "bg-primary text-primary-foreground" : "hover:bg-muted"
            }`}>
            {label}
          </button>
        ))}
      </div>

      <div className="p-4 space-y-5">
        {tab === "eco" && (
          <>
            <Section title={t("adminpanel.solde")} icon={Coins}>
              <div className="flex flex-wrap gap-1.5">
                {[500, 2000, 10000, 60000].map((n) => (
                  <Btn key={n} onClick={() => setBalance((b) => b + n)}>+{fmt(n)}</Btn>
                ))}
                <Btn tone="danger" onClick={() => setBalance(0)}>{t("adminpanel.remettre_a_0")}</Btn>
              </div>
              <input
                type="number"
                value={balance}
                onChange={(e) => setBalance(Math.max(0, +e.target.value || 0))}
                className="w-full rounded-lg border bg-background px-3 py-1.5 text-sm tabular-nums"
              />
            </Section>

            <Section title={t("rewards.coffres")} icon={Gift}>
              <div className="flex flex-wrap gap-1.5">
                <Btn onClick={() => onOpenBox("souk")}>{t("adminpanel.souk")}</Btn>
                <Btn onClick={() => onOpenBox("oasis")}>{t("adminpanel.oasis")}</Btn>
                <Btn onClick={() => onOpenBox("sultan")}>{t("adminpanel.sultan")}</Btn>
                <Btn onClick={() => setPity(24)}>{t("adminpanel.pitie_a_24")}</Btn>
                <Btn onClick={() => setPity(0)}>{t("adminpanel.pitie_a_0")}</Btn>
              </div>
              <p className="text-[11px] text-muted-foreground">{t("adminpanel.ouverture_gratuite_sans_debit_du_solde")}</p>
            </Section>

            <Section title={t("adminpanel.grille_de_prix")} icon={Coins}>
              <div className="space-y-1 text-xs">
                {RARITY_ORDER.map((r) => (
                  <div key={r} className="flex justify-between rounded-lg bg-muted px-2.5 py-1.5">
                    <span className="flex items-center gap-1.5">
                      <span className="w-2 h-2 rounded-full" style={{ background: RARITIES[r].color }} />
                      {RARITIES[r].label}
                    </span>
                    <span className="tabular-nums text-muted-foreground">
                      {RARITIES[r].boxOnly ? "coffres seulement" : `${fmt(RARITIES[r].price)} 🪙`}
                    </span>
                  </div>
                ))}
                <div className="flex justify-between rounded-lg bg-muted px-2.5 py-1.5">
                  <span>{t("adminpanel.parcelle_suivante")}</span>
                  <span className="tabular-nums text-muted-foreground">
                    {chunkPrice(island.chunks.length) === null ? "—" : `${fmt(chunkPrice(island.chunks.length))} 🪙`}
                  </span>
                </div>
              </div>
            </Section>
          </>
        )}

        {tab === "items" && (
          <>
            <Section title={t("adminpanel.creer_du_contenu")} icon={PlusSquare}>
              <Btn tone="primary" onClick={() => setCreateOpen(true)}>
                {t("adminpanel.ouvrir_l_atelier")}
              </Btn>
              <p className="text-[11px] text-muted-foreground">
                {t("adminpanel.ajouter_un_batiment_ou_un_sol")}
              </p>
            </Section>

            <Section title={t("adminpanel.images")} icon={ImageIcon}>
              <Btn tone="primary" onClick={() => setImagesOpen(true)}>
                {t("adminpanel.remplacer_une_image_de_batiment")}
              </Btn>
              <p className="text-[11px] text-muted-foreground">
                {t("adminpanel.fonctionne_aussi_pour_les_structures_de")}
              </p>
            </Section>

            <Section title={t("adminpanel.inventaire")} icon={Package}>
              <div className="flex flex-wrap gap-1.5">
                <Btn tone="primary" onClick={grantAll}>{t("adminpanel.tout_donner_3")}</Btn>
                <Btn tone="danger" onClick={() => setInventory({})}>{t("myerrors.vider")}</Btn>
              </div>
            </Section>
            <input
              value={filter}
              onChange={(e) => setFilter(e.target.value)}
              placeholder={t("adminpanel.filtrer_par_nom_ou_id")}
              className="w-full rounded-lg border bg-background px-3 py-1.5 text-sm"
            />
            <div className="space-y-1 max-h-72 overflow-y-auto">
              {items.map((i) => (
                <div key={i.id} className="flex items-center gap-2 rounded-lg bg-muted px-2.5 py-1.5 text-xs">
                  <span className="w-1.5 h-1.5 rounded-full shrink-0" style={{ background: RARITIES[i.rarity]?.color }} />
                  <span className="flex-1 min-w-0 truncate">
                    {i.name}
                    <span className="text-muted-foreground"> · {i.id}</span>
                    {i.size && (i.size[0] > 1 || i.size[1] > 1) && (
                      <span className="text-muted-foreground"> · {i.size[0]}×{i.size[1]}</span>
                    )}
                    {i.custom && <span className="text-sky-600"> {t("adminpanel.perso")}</span>}
                    {isNpc(i) && <span className="text-orange-600"> {t("adminpanel.habitant")}</span>}
                  </span>
                  <span className="tabular-nums text-muted-foreground shrink-0">{inventory[i.id] || 0}</span>
                  <Btn onClick={() => grant(i.id)}>+1</Btn>
                </div>
              ))}
            </div>
          </>
        )}

        {tab === "sets" && (
          <Section title={t("rewards.confreries")} icon={Gift}>
            <div className="space-y-2">
              {SETS.map((s) => {
                const pr = setProgress(s, inventory);
                const npcOwned = (inventory[s.npc.id] || 0) > 0;
                return (
                  <div key={s.id} className="rounded-xl bg-muted p-2.5 space-y-1.5">
                    <div className="flex items-center justify-between gap-2">
                      <span className="text-xs font-semibold">{s.name}</span>
                      <span className="text-[11px] tabular-nums text-muted-foreground">
                        {pr.owned}/{pr.total} {npcOwned ? "· habitant obtenu" : ""}
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1">
                      {s.buildings.map((bld) => (
                        <span key={bld.id}
                          className={`text-[10px] rounded px-1.5 py-0.5 ${
                            inventory[bld.id]
                              ? "bg-emerald-500/20 text-emerald-700 dark:text-emerald-300"
                              : "bg-background text-muted-foreground"
                          }`}>
                          {bld.name}
                        </span>
                      ))}
                      {s.buildings.length === 0 && (
                        <span className="text-[10px] text-muted-foreground">
                          {t("adminpanel.aucun_batiment_rattache_voir_l_atelier")}
                        </span>
                      )}
                    </div>
                    <Btn tone="primary" onClick={() => onGrantSet(s.id)}>{t("adminpanel.completer_cette_confrerie")}</Btn>
                  </div>
                );
              })}
            </div>
          </Section>
        )}

        {tab === "map" && (
          <>
            <Section title={t("adminpanel.parcelles")} icon={Map}>
              <div className="flex flex-wrap gap-1.5">
                {[1, 5, 15, MAX_CHUNKS].map((n) => (
                  <Btn key={n} onClick={() => addChunks(n)}>+{n}</Btn>
                ))}
              </div>
              <p className="text-[11px] text-muted-foreground tabular-nums">
                {island.chunks.length} / {MAX_CHUNKS} {t("adminpanel.parcelles_2")} {island.placed.length} {t("adminpanel.decors_poses")}
              </p>
            </Section>

            <Section title={t("adminpanel.ciel")} icon={Sun}>
              <div className="flex flex-wrap gap-1.5">
                <Btn tone={adminSky?.phase == null ? "primary" : "default"}
                  onClick={() => setAdminSky?.((s) => ({ ...s, phase: null }))}>
                  {t("adminpanel.cycle_auto")}{Math.round(CYCLE_MS / 60000)} {t("adminpanel.min")}
                </Btn>
              </div>
              <input type="range" min={0} max={1} step={0.001}
                value={adminSky?.phase ?? 0}
                onChange={(e) => setAdminSky?.((s) => ({ ...s, phase: +e.target.value }))}
                className="w-full accent-amber-500" />
              <p className="text-[11px] text-muted-foreground -mt-1">
                {t("adminpanel.fais_glisser_pour_figer_l_heure")}
              </p>
              <div className="flex flex-wrap gap-1.5">
                <Btn tone={adminSky?.rain === true ? "primary" : "default"}
                  onClick={() => setAdminSky?.((s) => ({ ...s, rain: s?.rain === true ? null : true }))}>
                  <CloudRain size={12} className="inline mr-1" /> {t("adminpanel.forcer_la_pluie")}
                </Btn>
                <Btn tone={adminSky?.rain === false ? "primary" : "default"}
                  onClick={() => setAdminSky?.((s) => ({ ...s, rain: s?.rain === false ? null : false }))}>
                  {t("adminpanel.forcer_le_beau_temps")}
                </Btn>
              </div>
            </Section>

            <Section title={t("adminpanel.ile")} icon={RotateCcw}>
              <Btn tone="danger"
                onClick={() => {
                  if (window.confirm("Retirer tous les décors et le terrain peint ?")) onResetIsland?.();
                }}>
                {t("rewards.redemarrer_l_ile")}
              </Btn>
            </Section>

            <Section title={t("adminpanel.calage_des_sprites")} icon={Map}>
              <Btn tone={calibrate ? "primary" : "default"} onClick={() => setCalibrate?.(!calibrate)}>
                {calibrate ? "Masquer" : "Afficher"} {t("adminpanel.les_cases_logiques")}
              </Btn>
              <p className="text-[11px] text-muted-foreground">
                {t("adminpanel.trace_un_losange_rouge_sur_la")} <code>{t("adminpanel.tile_anchor")}</code> dans{" "}
                <code>{t("adminpanel.tileset_js")}</code> {t("adminpanel.pour_une_image_personnalisee_le_curseur")}
              </p>
            </Section>

            <Section title={t("adminpanel.nettoyage")} icon={Trash2}>
              <div className="flex flex-wrap gap-1.5">
                <Btn tone="danger" onClick={() => setIsland((is) => ({ ...is, placed: [] }))}>
                  {t("adminpanel.retirer_tous_les_decors")}
                </Btn>
                <Btn tone="danger" onClick={() => setIsland((is) => ({ ...is, terrain: {} }))}>
                  {t("adminpanel.reinitialiser_le_terrain")}
                </Btn>
              </div>
            </Section>

            <Section title={t("adminpanel.etat")} icon={Copy}>
              <Btn onClick={copyState}>
                {copied ? (<><Check size={12} className="inline" /> {t("rewards.copie")}</>) : "Copier le JSON de l'état"}
              </Btn>
            </Section>
          </>
        )}

        <p className="text-[11px] text-muted-foreground border-t pt-3">
          {t("adminpanel.solde_inventaire_et_parcelles_ne_sont")}
        </p>
      </div>

      {imagesOpen && (
        <AdminImages onClose={() => setImagesOpen(false)} onChanged={onImagesChanged} />
      )}
      {createOpen && (
        <AdminCreate onClose={() => setCreateOpen(false)} onChanged={onImagesChanged} />
      )}
    </div>
  );
}
